import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Ban, Check, Edit, Eye, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { permissionsApi } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import type {
    GrantPolicyPermissionBundleRequest,
    ListPolicyPermissionGrantsResponse,
    PermissionsGrant,
    PermissionsBundlePublic,
    PolicyPermissionBundlesSearchPageResponse,
} from '@anchorbase/pillar-sdk';
import { Status, type Role } from '@anchorbase/pillar-sdk';
import { PolicyPermissionBundleModal } from './PolicyPermissionBundleModal';
import { PermissionGrantSummary } from './PermissionGrantSummary';

interface PolicyGrantAssignableRoleAssignment {
    role_assignment_uuid: string;
    role: Role;
    organization_uuid: string | null;
    organization_name: string | null;
    permissions_bundle: PermissionsBundlePublic;
}

interface PolicyPermissionGrantsCardProps {
    roleAssignments: ReadonlyArray<PolicyGrantAssignableRoleAssignment>;
    disabled?: boolean;
}

function excludeDeletedBundles(
    bundles: ReadonlyArray<PermissionsBundlePublic>
): ReadonlyArray<PermissionsBundlePublic> {
    return bundles.filter((bundle) => bundle.status !== Status.DELETED);
}

function formatRoleAssignmentLabel(
    roleAssignment: PolicyGrantAssignableRoleAssignment
): string {
    if (roleAssignment.organization_uuid === null) {
        return `${roleAssignment.role} (internal)`;
    }
    const orgName: string = roleAssignment.organization_name ?? 'Organization';
    return `${roleAssignment.role} @ ${orgName}`;
}

function getRequiredRoleAssignmentUuid(
    roleAssignment: PolicyGrantAssignableRoleAssignment
): string {
    if (roleAssignment.role_assignment_uuid === undefined) {
        throw new Error('Missing role assignment uuid');
    }
    return roleAssignment.role_assignment_uuid;
}

function getRequiredOrganizationUuid(
    roleAssignment: PolicyGrantAssignableRoleAssignment
): string {
    if (roleAssignment.organization_uuid === undefined || roleAssignment.organization_uuid === null) {
        throw new Error('Missing organization uuid');
    }
    return roleAssignment.organization_uuid;
}

function moveBundle(
    bundles: ReadonlyArray<PermissionsBundlePublic>,
    sourceUuid: string,
    targetUuid: string
): ReadonlyArray<PermissionsBundlePublic> {
    const sourceIndex = bundles.findIndex(
        (bundle) => bundle.permissions_bundle_uuid === sourceUuid
    );
    const targetIndex = bundles.findIndex(
        (bundle) => bundle.permissions_bundle_uuid === targetUuid
    );
    if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex === targetIndex
    ) {
        return bundles;
    }
    const nextBundles = [...bundles];
    const [movedBundle] = nextBundles.splice(sourceIndex, 1);
    if (movedBundle === undefined) {
        return bundles;
    }
    nextBundles.splice(targetIndex, 0, movedBundle);
    return nextBundles;
}

function updateBundleStatusInList(
    bundles: ReadonlyArray<PermissionsBundlePublic>,
    permissionsBundleUuid: string,
    status: Status
): ReadonlyArray<PermissionsBundlePublic> {
    return bundles.map((bundle) =>
        bundle.permissions_bundle_uuid === permissionsBundleUuid
            ? {
                  ...bundle,
                  status,
              }
            : bundle
    );
}

function bundleOrderChanged(
    baseline: ReadonlyArray<PermissionsBundlePublic>,
    current: ReadonlyArray<PermissionsBundlePublic>
): boolean {
    if (baseline.length !== current.length) {
        return true;
    }
    return baseline.some((bundle, index) => {
        return bundle.permissions_bundle_uuid !== current[index]?.permissions_bundle_uuid;
    });
}

interface EffectivePermissionBundleGroup {
    permissions_bundle_uuid: string;
    name: string;
    is_personal: boolean;
    permissions: ReadonlyArray<PermissionsGrant>;
}

interface PolicyEffectivePermissionsCardProps {
    roleAssignments: ReadonlyArray<PolicyGrantAssignableRoleAssignment>;
}

export function PolicyEffectivePermissionsCard(
    props: PolicyEffectivePermissionsCardProps
) {
    const orgScopedRoleAssignments: ReadonlyArray<PolicyGrantAssignableRoleAssignment> =
        useMemo(() => {
            return props.roleAssignments.filter(
                (roleAssignment: PolicyGrantAssignableRoleAssignment) =>
                    roleAssignment.organization_uuid !== null
            );
        }, [props.roleAssignments]);

    const [selectedRoleAssignmentUuid, setSelectedRoleAssignmentUuid] = useState<string | null>(
        () => {
            const first: PolicyGrantAssignableRoleAssignment | undefined =
                orgScopedRoleAssignments[0];
            return first ? getRequiredRoleAssignmentUuid(first) : null;
        }
    );

    useEffect(() => {
        if (selectedRoleAssignmentUuid !== null) {
            return;
        }
        const first: PolicyGrantAssignableRoleAssignment | undefined =
            orgScopedRoleAssignments[0];
        if (!first) {
            return;
        }
        setSelectedRoleAssignmentUuid(getRequiredRoleAssignmentUuid(first));
    }, [orgScopedRoleAssignments, selectedRoleAssignmentUuid]);

    const selectedRoleAssignment: PolicyGrantAssignableRoleAssignment | null =
        useMemo(() => {
            if (selectedRoleAssignmentUuid === null) {
                return null;
            }
            const found: PolicyGrantAssignableRoleAssignment | undefined =
                orgScopedRoleAssignments.find(
                    (roleAssignment: PolicyGrantAssignableRoleAssignment) =>
                        roleAssignment.role_assignment_uuid === selectedRoleAssignmentUuid
                );
            return found ?? null;
        }, [orgScopedRoleAssignments, selectedRoleAssignmentUuid]);

    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const policyGrantsVersion: number = useDataInvalidationStore(
        (state) => state.policyGrantsVersion
    );

    const { data: grantsData, isLoading: isGrantsLoading, error: grantsError,
    } =
        useQuery<ListPolicyPermissionGrantsResponse>({
            queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'policyPermissionGrants',
                selectedRoleAssignmentUuid ?? 'none',
                policyGrantsVersion,
            ]),
            queryFn: async () => {
                if (selectedRoleAssignmentUuid === null) {
                    throw new Error('Missing role assignment uuid');
                }
                return await permissionsApi.listPolicyPermissionGrantsForRoleAssignment(
                    selectedRoleAssignmentUuid
                );
            },
            enabled: selectedRoleAssignmentUuid !== null,
            staleTime: 0,
        });

    const savedPriorityBundles: ReadonlyArray<PermissionsBundlePublic> = useMemo(() => {
        return excludeDeletedBundles(grantsData?.bundles ?? []);
    }, [grantsData?.bundles]);

    const effectivePermissionBundleGroups: ReadonlyArray<EffectivePermissionBundleGroup> =
        useMemo(() => {
            if (selectedRoleAssignment === null) {
                return [];
            }
            return [
                {
                    permissions_bundle_uuid:
                        selectedRoleAssignment.permissions_bundle.permissions_bundle_uuid,
                    name: selectedRoleAssignment.permissions_bundle.name,
                    is_personal: true,
                    permissions: selectedRoleAssignment.permissions_bundle.permissions,
                },
                ...savedPriorityBundles.map((bundle) => ({
                    permissions_bundle_uuid: bundle.permissions_bundle_uuid,
                    name: bundle.name,
                    is_personal: false,
                    permissions: bundle.permissions,
                })),
            ];
        }, [savedPriorityBundles, selectedRoleAssignment]);

    if (orgScopedRoleAssignments.length === 0) {
        return null;
    }

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>Effective Permissions</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                    Review personal and granted policy permissions in evaluation order.
                </p>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-2">
                    <div className="text-sm font-medium">Role assignment</div>
                    <select
                        className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={selectedRoleAssignmentUuid ?? ''}
                        onChange={(e) => {
                            const nextUuid = e.target.value.trim();
                            setSelectedRoleAssignmentUuid(nextUuid.length > 0 ? nextUuid : null);
                        }}
                    >
                        {orgScopedRoleAssignments.map(
                            (roleAssignment: PolicyGrantAssignableRoleAssignment) => {
                                const roleAssignmentUuid: string =
                                    getRequiredRoleAssignmentUuid(roleAssignment);
                                return (
                                    <option key={roleAssignmentUuid} value={roleAssignmentUuid}>
                                        {formatRoleAssignmentLabel(roleAssignment)}
                                    </option>
                                );
                            }
                        )}
                    </select>
                </div>

                <div className="space-y-3">
                    {isGrantsLoading ? (
                        <p className="text-sm text-muted-foreground">Loading...</p>
                    ) : grantsError instanceof Error ? (
                        <Alert variant="destructive">
                            <AlertDescription>{grantsError.message}</AlertDescription>
                        </Alert>
                    ) : effectivePermissionBundleGroups.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No effective permissions to display.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {effectivePermissionBundleGroups.map((bundleGroup) => (
                                <div
                                    key={bundleGroup.permissions_bundle_uuid}
                                    className="border rounded-md p-3 space-y-3"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="font-medium">{bundleGroup.name}</div>
                                        <Badge variant="secondary">
                                            {bundleGroup.is_personal ? 'Personal' : 'Policy'}
                                        </Badge>
                                    </div>
                                    {bundleGroup.permissions.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">
                                            No permissions in this bundle.
                                        </p>
                                    ) : (
                                        <div className="space-y-3">
                                            {bundleGroup.permissions.map((permission, index) => (
                                                <PermissionGrantSummary
                                                    key={`${bundleGroup.permissions_bundle_uuid}-${index}`}
                                                    permission={permission}
                                                    compact
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export function PolicyPermissionGrantsCard(props: PolicyPermissionGrantsCardProps) {
    const navigate = useNavigate();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const policyGrantsVersion: number = useDataInvalidationStore(
        (state) => state.policyGrantsVersion
    );
    const bumpPolicyGrantsVersion = useDataInvalidationStore(
        (state) => state.bumpPolicyGrantsVersion
    );

    const orgScopedRoleAssignments: ReadonlyArray<PolicyGrantAssignableRoleAssignment> =
        useMemo(() => {
        return props.roleAssignments.filter(
            (roleAssignment: PolicyGrantAssignableRoleAssignment) =>
                roleAssignment.organization_uuid !== null
        );
    }, [props.roleAssignments]);

    const [selectedRoleAssignmentUuid, setSelectedRoleAssignmentUuid] = useState<string | null>(
        () => {
            const first: PolicyGrantAssignableRoleAssignment | undefined =
                orgScopedRoleAssignments[0];
            return first ? getRequiredRoleAssignmentUuid(first) : null;
        }
    );

    useEffect(() => {
        if (selectedRoleAssignmentUuid !== null) {
            return;
        }
        const first: PolicyGrantAssignableRoleAssignment | undefined =
            orgScopedRoleAssignments[0];
        if (!first) {
            return;
        }
        setSelectedRoleAssignmentUuid(getRequiredRoleAssignmentUuid(first));
    }, [orgScopedRoleAssignments, selectedRoleAssignmentUuid]);

    const selectedRoleAssignment: PolicyGrantAssignableRoleAssignment | null =
        useMemo(() => {
        if (selectedRoleAssignmentUuid === null) {
            return null;
        }
        const found: PolicyGrantAssignableRoleAssignment | undefined =
            orgScopedRoleAssignments.find(
            (roleAssignment: PolicyGrantAssignableRoleAssignment) =>
                roleAssignment.role_assignment_uuid === selectedRoleAssignmentUuid
        );
        return found ?? null;
    }, [orgScopedRoleAssignments, selectedRoleAssignmentUuid]);

    const selectedRoleAssignmentUuidRequired: string | null =
        selectedRoleAssignment === null ? null : getRequiredRoleAssignmentUuid(selectedRoleAssignment);
    const selectedOrganizationUuid: string | null =
        selectedRoleAssignment === null ? null : getRequiredOrganizationUuid(selectedRoleAssignment);
    const selectedOrganizationName: string =
        selectedRoleAssignment?.organization_name ?? 'Organization';

    const { data: grantsData, isLoading: isGrantsLoading, error: grantsError,
    } =
        useQuery<ListPolicyPermissionGrantsResponse>({
            queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'policyPermissionGrants',
                selectedRoleAssignmentUuid ?? 'none',
                policyGrantsVersion,
            ]),
            queryFn: async () => {
                if (selectedRoleAssignmentUuid === null) {
                    throw new Error('Missing role assignment uuid');
                }
                return await permissionsApi.listPolicyPermissionGrantsForRoleAssignment(
                    selectedRoleAssignmentUuid
                );
            },
            enabled: selectedRoleAssignmentUuid !== null,
            staleTime: 0,
        });

    const grantedBundles: ReadonlyArray<PermissionsBundlePublic> = useMemo(() => {
        return excludeDeletedBundles(grantsData?.bundles ?? []);
    }, [grantsData?.bundles]);
    const [isEditingPriorities, setIsEditingPriorities] = useState<boolean>(false);
    const [priorityDraftBundles, setPriorityDraftBundles] = useState<
        ReadonlyArray<PermissionsBundlePublic>
    >([]);
    const [savedPriorityBundles, setSavedPriorityBundles] = useState<
        ReadonlyArray<PermissionsBundlePublic>
    >([]);
    const [draggedBundleUuid, setDraggedBundleUuid] = useState<string | null>(null);

    useEffect(() => {
        setPriorityDraftBundles(grantedBundles);
        setSavedPriorityBundles(grantedBundles);
        setIsEditingPriorities(false);
        setDraggedBundleUuid(null);
    }, [grantedBundles, selectedRoleAssignmentUuid]);

    const displayedGrantedBundles: ReadonlyArray<PermissionsBundlePublic> = isEditingPriorities
        ? priorityDraftBundles
        : savedPriorityBundles;
    const hasPendingPriorityChanges: boolean = bundleOrderChanged(
        savedPriorityBundles,
        priorityDraftBundles
    );
    const [selectedPolicyBundleUuid, setSelectedPolicyBundleUuid] = useState<string | null>(null);
    const [pendingRevokeBundleUuid, setPendingRevokeBundleUuid] = useState<string | null>(null);
    const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState<boolean>(false);
    const [selectedBundle, setSelectedBundle] = useState<PermissionsBundlePublic | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

    useUnsavedChangesRegistration(isEditingPriorities || isEditModalOpen);

    useEffect(() => {
        setSelectedPolicyBundleUuid(null);
    }, [selectedRoleAssignmentUuid]);

    const grantMutation = useMutation({
        mutationFn: async () => {
            if (selectedRoleAssignmentUuidRequired === null) {
                throw new Error('Missing role assignment uuid');
            }
            if (selectedPolicyBundleUuid === null) {
                throw new Error('Select a policy bundle to grant');
            }
            const payload: GrantPolicyPermissionBundleRequest = {
                permissions_bundle_uuid: selectedPolicyBundleUuid,
            };
            return await permissionsApi.grantPolicyPermissionBundleToRoleAssignment(
                selectedRoleAssignmentUuidRequired,
                payload
            );
        },
        onSuccess: () => {
            bumpPolicyGrantsVersion();
            setSelectedPolicyBundleUuid(null);
        },
        onError: (err) => {
            console.error('Error granting policy bundle:', err);
        },
    });

    const revokeMutation = useMutation({
        mutationFn: async (bundleUuid: string) => {
            if (selectedRoleAssignmentUuidRequired === null) {
                throw new Error('Missing role assignment uuid');
            }
            return await permissionsApi.revokePolicyPermissionBundleFromRoleAssignment(
                selectedRoleAssignmentUuidRequired,
                bundleUuid
            );
        },
        onSuccess: () => {
            bumpPolicyGrantsVersion();
        },
        onError: (err) => {
            console.error('Error revoking policy bundle:', err);
        },
    });

    const reorderMutation = useMutation({
        mutationFn: async (orderedBundleUuids: ReadonlyArray<string>) => {
            if (selectedRoleAssignmentUuidRequired === null) {
                throw new Error('Missing role assignment uuid');
            }
            return await permissionsApi.reorderPolicyPermissionGrantsForRoleAssignment(
                selectedRoleAssignmentUuidRequired,
                {
                    permissions_bundle_uuids: [...orderedBundleUuids],
                }
            );
        },
        onSuccess: (_result, orderedBundleUuids) => {
            const nextSavedBundles = orderedBundleUuids
                .map((bundleUuid) =>
                    priorityDraftBundles.find(
                        (bundle) => bundle.permissions_bundle_uuid === bundleUuid
                    )
                )
                .filter((bundle): bundle is PermissionsBundlePublic => bundle !== undefined);
            setSavedPriorityBundles(nextSavedBundles);
            setPriorityDraftBundles(nextSavedBundles);
            setIsEditingPriorities(false);
            setDraggedBundleUuid(null);
            bumpPolicyGrantsVersion();
        },
        onError: (err) => {
            console.error('Error reordering policy bundles:', err);
        },
    });

    const setActiveMutation = useMutation({
        mutationFn: async (params: {
            permissionsBundleUuid: string;
            isActive: boolean }) => {
            await permissionsApi.updatePolicyPermissionBundle(
                params.permissionsBundleUuid,
                { status: params.isActive ? Status.ACTIVE : Status.DISABLED,
            }
            );
        },
        onSuccess: (_response, variables) => {
            const nextStatus: Status = variables.isActive ? Status.ACTIVE : Status.DISABLED;
            setSavedPriorityBundles((currentValue) =>
                updateBundleStatusInList(
                    currentValue,
                    variables.permissionsBundleUuid,
                    nextStatus
                )
            );
            setPriorityDraftBundles((currentValue) =>
                updateBundleStatusInList(
                    currentValue,
                    variables.permissionsBundleUuid,
                    nextStatus
                )
            );
            setSelectedBundle((currentValue) =>
                currentValue !== null &&
                currentValue.permissions_bundle_uuid === variables.permissionsBundleUuid
                    ? {
                          ...currentValue,
                          status: nextStatus,
                      }
                    : currentValue
            );
        },
        onError: (err) => {
            console.error('Error updating policy bundle:', err);
        },
    });

    return (
        <>
            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Permissions Policy Grants</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            Grant and revoke policy bundles per role assignment.
                        </p>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {orgScopedRoleAssignments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            This user has no organization-scoped role assignments.
                        </p>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <div className="text-sm font-medium">Role assignment</div>
                                <select
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedRoleAssignmentUuid ?? ''}
                                    disabled={props.disabled === true}
                                    onChange={(e) => {
                                        const nextUuid = e.target.value.trim();
                                        setSelectedRoleAssignmentUuid(
                                            nextUuid.length > 0 ? nextUuid : null
                                        );
                                    }}
                                >
                                    {orgScopedRoleAssignments.map(
                                        (
                                            roleAssignment: PolicyGrantAssignableRoleAssignment
                                        ) => {
                                            const roleAssignmentUuid: string =
                                                getRequiredRoleAssignmentUuid(roleAssignment);
                                            return (
                                                <option
                                                    key={roleAssignmentUuid}
                                                    value={roleAssignmentUuid}
                                                >
                                                    {formatRoleAssignmentLabel(roleAssignment)}
                                                </option>
                                            );
                                        }
                                    )}
                                </select>
                            </div>

                            <div className="flex items-end gap-4">
                                <div className="flex-1">
                                    <OmnisearchSelect<PermissionsBundlePublic, string>
                                        label="+ Policy"
                                        placeholder="Search policy bundles..."
                                        pageSize={25}
                                        disabled={
                                            props.disabled === true ||
                                            selectedOrganizationUuid === null ||
                                            selectedRoleAssignmentUuid === null
                                        }
                                        resetToken={`org=${selectedOrganizationUuid ?? 'none'}`}
                                        fetchPage={async (params) => {
                                            if (selectedOrganizationUuid === null) {
                                                throw new Error('Missing organization uuid');
                                            }
                                            const response: PolicyPermissionBundlesSearchPageResponse =
                                                await permissionsApi.searchPolicyPermissionBundlesForOrganization(
                                                    selectedOrganizationUuid,
                                                    {
                                                        ...(params.terms !== undefined
                                                            ? { terms: [...params.terms] }
                                                            : {}),
                                                        limit: params.limit,
                                                        cursor: params.cursor,
                                                    }
                                                );
                                            return {
                                                items: excludeDeletedBundles(response.bundles),
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(bundle) => bundle.permissions_bundle_uuid}
                                        getItemLabel={(bundle) => bundle.name}
                                        getItemFilterText={(bundle) =>
                                            `${bundle.name} ${bundle.description ?? ''} ${bundle.permissions_bundle_uuid}`
                                                .trim()
                                                .toLowerCase()
                                        }
                                        selectedItemId={selectedPolicyBundleUuid}
                                        onClearSelection={() => {
                                            setSelectedPolicyBundleUuid(null);
                                        }}
                                        onSelect={(bundle) =>
                                            setSelectedPolicyBundleUuid(
                                                bundle.permissions_bundle_uuid
                                            )
                                        }
                                    />
                                </div>
                                <Button
                                    onClick={() => grantMutation.mutate()}
                                    disabled={
                                        props.disabled === true ||
                                        selectedRoleAssignmentUuid === null ||
                                        selectedPolicyBundleUuid === null ||
                                        grantMutation.isPending
                                    }
                                >
                                    {grantMutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                            </div>

                            {grantMutation.error instanceof Error && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {grantMutation.error.message}
                                    </AlertDescription>
                                </Alert>
                            )}

                            {reorderMutation.error instanceof Error && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {reorderMutation.error.message}
                                    </AlertDescription>
                                </Alert>
                            )}

                            {setActiveMutation.error instanceof Error && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {setActiveMutation.error.message}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-medium">Granted policies</div>
                                    {displayedGrantedBundles.length > 0 ? (
                                        isEditingPriorities ? (
                                            <div className="flex items-center gap-2">
                                                <span
                                                    title={
                                                        hasPendingPriorityChanges
                                                            ? undefined
                                                            : 'Save is disabled because the policy ordering is unchanged.'
                                                    }
                                                >
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        disabled={
                                                            hasPendingPriorityChanges === false ||
                                                            reorderMutation.isPending
                                                        }
                                                        onClick={() => {
                                                            void reorderMutation.mutate(
                                                                priorityDraftBundles.map(
                                                                    (bundle) =>
                                                                        bundle.permissions_bundle_uuid
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        {reorderMutation.isPending
                                                            ? 'Saving...'
                                                            : 'Save'}
                                                    </Button>
                                                </span>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setPriorityDraftBundles(
                                                            savedPriorityBundles
                                                        );
                                                        setIsEditingPriorities(false);
                                                        setDraggedBundleUuid(null);
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setPriorityDraftBundles(savedPriorityBundles);
                                                    setIsEditingPriorities(true);
                                                }}
                                            >
                                                Edit priorities
                                            </Button>
                                        )
                                    ) : null}
                                </div>

                                {isGrantsLoading ? (
                                    <p className="text-sm text-muted-foreground">Loading...</p>
                                ) : grantsError instanceof Error ? (
                                    <Alert variant="destructive">
                                        <AlertDescription>{grantsError.message}</AlertDescription>
                                    </Alert>
                                ) : grantedBundles.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        No policy bundles granted.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {displayedGrantedBundles.map(
                                            (bundle: PermissionsBundlePublic) => (
                                            <div
                                                key={bundle.permissions_bundle_uuid}
                                                className="flex items-center justify-between border rounded-md p-3"
                                                draggable={isEditingPriorities}
                                                onDragStart={() => {
                                                    if (!isEditingPriorities) {
                                                        return;
                                                    }
                                                    setDraggedBundleUuid(
                                                        bundle.permissions_bundle_uuid
                                                    );
                                                }}
                                                onDragOver={(event) => {
                                                    if (!isEditingPriorities) {
                                                        return;
                                                    }
                                                    event.preventDefault();
                                                }}
                                                onDrop={() => {
                                                    if (
                                                        !isEditingPriorities ||
                                                        draggedBundleUuid === null
                                                    ) {
                                                        return;
                                                    }
                                                    setPriorityDraftBundles((currentBundles) =>
                                                        moveBundle(
                                                            currentBundles,
                                                            draggedBundleUuid,
                                                            bundle.permissions_bundle_uuid
                                                        )
                                                    );
                                                    setDraggedBundleUuid(null);
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-3 text-left"
                                                    onClick={() => {
                                                        if (selectedOrganizationUuid === null) {
                                                            return;
                                                        }
                                                        Promise.resolve(
                                                            navigate(
                                                                `/organizations/${selectedOrganizationUuid}/permissions-policies/${bundle.permissions_bundle_uuid}/users`
                                                            )
                                                        ).catch(() => {});
                                                    }}
                                                >
                                                    <div className="font-medium">
                                                        {bundle.name}
                                                    </div>
                                                    <Badge variant="secondary">
                                                        {bundle.permissions.length} permissions
                                                        </Badge>
                                                    {bundle.status !== Status.ACTIVE ? (
                                                        <Badge variant="destructive">
                                                            {bundle.status === Status.DELETED
                                                                ? 'Deleted'
                                                                : 'Disabled'}
                                                        </Badge>
                                                    ) : null}
                                                </button>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        title="View policy bundle"
                                                        onClick={() => {
                                                            setSelectedBundle(bundle);
                                                            setIsViewModalOpen(true);
                                                        }}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        title="Edit policy bundle"
                                                        onClick={() => {
                                                            setSelectedBundle(bundle);
                                                            setIsEditModalOpen(true);
                                                        }}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        title={
                                                            bundle.status === Status.ACTIVE
                                                                ? 'Disable policy bundle'
                                                                : 'Enable policy bundle'
                                                        }
                                                        disabled={
                                                            props.disabled === true ||
                                                            isEditingPriorities ||
                                                            setActiveMutation.isPending
                                                        }
                                                        onClick={() => {
                                                            setActiveMutation.mutate({
                                                                permissionsBundleUuid:
                                                                    bundle.permissions_bundle_uuid,
                                                                isActive:
                                                                    bundle.status !==
                                                                    Status.ACTIVE,
                                                            });
                                                        }}
                                                    >
                                                        {bundle.status === Status.ACTIVE ? (
                                                            <Ban className="h-4 w-4" />
                                                        ) : (
                                                            <Check className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        title="Manage policy users"
                                                        onClick={() => {
                                                            if (selectedOrganizationUuid === null) {
                                                                return;
                                                            }
                                                            Promise.resolve(
                                                                navigate(
                                                                    `/organizations/${selectedOrganizationUuid}/permissions-policies/${bundle.permissions_bundle_uuid}/users`
                                                                )
                                                            ).catch(() => {});
                                                        }}
                                                    >
                                                        <User className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={
                                                            props.disabled === true ||
                                                            revokeMutation.isPending ||
                                                            isEditingPriorities
                                                        }
                                                        onClick={() => {
                                                            setPendingRevokeBundleUuid(
                                                                bundle.permissions_bundle_uuid
                                                            );
                                                            setIsRevokeConfirmOpen(true);
                                                        }}
                                                    >
                                                            -
                                                        </Button>
                                                </div>
                                            </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>

                        </>
                    )}
                </CardContent>
            </Card>

            <ConfirmationModal
                open={isRevokeConfirmOpen}
                onOpenChange={setIsRevokeConfirmOpen}
                message="Revoke this policy bundle?"
                confirmLabel="Revoke"
                isLoading={revokeMutation.isPending}
                onConfirm={() => {
                    if (pendingRevokeBundleUuid === null) {
                        return;
                    }
                    setIsRevokeConfirmOpen(false);
                    revokeMutation.mutate(pendingRevokeBundleUuid);
                    setPendingRevokeBundleUuid(null);
                }}
                onCancel={() => {
                    setPendingRevokeBundleUuid(null);
                    setIsRevokeConfirmOpen(false);
                }}
            />

            <PolicyPermissionBundleModal
                open={isViewModalOpen}
                onOpenChange={setIsViewModalOpen}
                mode="view"
                organizationId={null}
                organizationUuid={selectedOrganizationUuid ?? ''}
                organizationName={selectedOrganizationName}
                bundle={selectedBundle}
            />

            <PolicyPermissionBundleModal
                open={isEditModalOpen}
                onOpenChange={setIsEditModalOpen}
                mode="edit"
                organizationId={null}
                organizationUuid={selectedOrganizationUuid ?? ''}
                organizationName={selectedOrganizationName}
                bundle={selectedBundle}
            />
        </>
    );
}
